import { useGT } from 'gt-react';
import {
  formatReportNumber,
  isReportFilterActive,
  reportDatePresetRange,
  type ReportDatePreset,
  type ReportFilter,
} from '@erp/shared-types';
import { useDatePresetLabels } from './ReportFilterBar';

/** The most values a summary names before "and N more". */
const LISTED_VALUES = 5;

const dayFormat = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

function day(iso: string): string {
  return dayFormat.format(new Date(`${iso}T00:00:00Z`));
}

type Gt = ReturnType<typeof useGT>;
type Filter<K extends ReportFilter['kind']> = Extract<
  ReportFilter,
  { kind: K }
>;

function describeValues(gt: Gt, filter: Filter<'values'>): string {
  const shown = filter.values
    .slice(0, LISTED_VALUES)
    .map((value) =>
      value === ''
        ? gt('(Empty)', { context: 'report filter empty value' })
        : value,
    )
    .join(', ');
  const more = filter.values.length - LISTED_VALUES;
  return more > 0
    ? gt('{values} and {count} more', {
        values: shown,
        count: more,
        context: 'report filter summary',
      })
    : shown;
}

function describeDates(gt: Gt, range: { from?: string; to?: string }): string {
  if (range.from && range.to) return `${day(range.from)} – ${day(range.to)}`;
  return range.from
    ? gt('on or after {date}', {
        date: day(range.from),
        context: 'report filter summary',
      })
    : gt('on or before {date}', {
        date: day(range.to ?? ''),
        context: 'report filter summary',
      });
}

function describeDateRange(
  gt: Gt,
  presets: Record<ReportDatePreset, string>,
  filter: Filter<'date-range'>,
  today: Date,
): string {
  if (!filter.preset) return describeDates(gt, filter);
  // The viewer's calendar day, as the presets count in it.
  const utcDay = new Date(
    Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()),
  );
  const dates = describeDates(gt, reportDatePresetRange(filter.preset, utcDay));
  return `${presets[filter.preset]} (${dates})`;
}

function describeNumberRange(gt: Gt, filter: Filter<'number-range'>): string {
  const n = (value: number) => formatReportNumber(value, {});
  if (filter.min !== undefined && filter.max !== undefined)
    return gt('{min} to {max}', {
      min: n(filter.min),
      max: n(filter.max),
      context: 'report filter summary',
    });
  return filter.min !== undefined
    ? gt('at least {min}', {
        min: n(filter.min),
        context: 'report filter summary',
      })
    : gt('at most {max}', {
        max: n(filter.max ?? 0),
        context: 'report filter summary',
      });
}

/**
 * The Report filters in effect in words, for a PDF's header — "Filtered to
 * Grade: A · Ship date: Sep 1, 2026 – Sep 30, 2026" — as Looker Studio and
 * Power BI print a report's filter state, so its numbers aren't read as
 * totals. Null when no filter narrows anything.
 */
export function useReportFilterSummary() {
  const gt = useGT();
  const presets = useDatePresetLabels();
  const describe = (filter: ReportFilter, today: Date): string => {
    switch (filter.kind) {
      case 'values':
        return describeValues(gt, filter);
      case 'date-range':
        return describeDateRange(gt, presets, filter, today);
      case 'number-range':
        return describeNumberRange(gt, filter);
    }
  };
  return (
    filters: readonly ReportFilter[] | undefined,
    today: Date,
    labelOf: (filter: ReportFilter) => string,
  ): string | null => {
    const active = (filters ?? []).filter(isReportFilterActive);
    if (!active.length) return null;
    return gt('Filtered to {filters}', {
      filters: active
        .map((filter) => `${labelOf(filter)}: ${describe(filter, today)}`)
        .join(' · '),
      context: 'report PDF header filter summary',
    });
  };
}
